import type { ArtifactResponse } from '@/client/types.gen'
import type {
	PreviewMetadataTab,
	TranscriptViewerTab,
} from '@/components/project/artifact-viewer/ViewerTabs'
import type { PreviewCategory } from '@/types/artifacts'
import { isTranscriptMediaCategory } from '@/types/artifacts'

/** Discriminated preview header slot state (presentational — callbacks wired by ArtifactPreviewPanel). */
export type PreviewHeaderState =
	| {
			kind: 'transcript'
			artifact: ArtifactResponse
			activeTab: TranscriptViewerTab
			onTabChange: (tab: TranscriptViewerTab) => void
			canExportPdf: boolean
			onExportPdf?: () => Promise<void>
			exportingPdf: boolean
	  }
	| {
			kind: 'pdf'
			artifact: ArtifactResponse
			activeTab: PreviewMetadataTab
			onTabChange: (tab: PreviewMetadataTab) => void
	  }
	| {
			kind: 'image'
			artifact: ArtifactResponse
			activeTab: PreviewMetadataTab
			onTabChange: (tab: PreviewMetadataTab) => void
	  }
	| {
			kind: 'text'
			artifact: ArtifactResponse
			activeTab: PreviewMetadataTab
			onTabChange: (tab: PreviewMetadataTab) => void
	  }
	| {
			kind: 'bpmn'
			artifact: ArtifactResponse
			activeTab: PreviewMetadataTab
			onTabChange: (tab: PreviewMetadataTab) => void
	  }
	| {
			kind: 'unsupported'
			artifact: ArtifactResponse
	  }

export function buildPreviewHeaderState(
	artifact: ArtifactResponse,
	category: PreviewCategory,
	tabState: {
		transcriptActiveTab: TranscriptViewerTab
		onTranscriptTabChange: (tab: TranscriptViewerTab) => void
		pdfActiveTab: PreviewMetadataTab
		onPdfTabChange: (tab: PreviewMetadataTab) => void
		imageActiveTab: PreviewMetadataTab
		onImageTabChange: (tab: PreviewMetadataTab) => void
		textActiveTab: PreviewMetadataTab
		onTextTabChange: (tab: PreviewMetadataTab) => void
	},
	transcriptExport: {
		canExportPdf: boolean
		onExportPdf?: () => Promise<void>
		exportingPdf: boolean
	}
): PreviewHeaderState {
	if (isTranscriptMediaCategory(category)) {
		return {
			kind: 'transcript',
			artifact,
			activeTab: tabState.transcriptActiveTab,
			onTabChange: tabState.onTranscriptTabChange,
			canExportPdf: transcriptExport.canExportPdf,
			onExportPdf: transcriptExport.onExportPdf,
			exportingPdf: transcriptExport.exportingPdf,
		}
	}
	switch (category) {
		case 'pdf':
			return {
				kind: 'pdf',
				artifact,
				activeTab: tabState.pdfActiveTab,
				onTabChange: tabState.onPdfTabChange,
			}
		case 'image':
			return {
				kind: 'image',
				artifact,
				activeTab: tabState.imageActiveTab,
				onTabChange: tabState.onImageTabChange,
			}
		case 'text':
			return {
				kind: 'text',
				artifact,
				activeTab: tabState.textActiveTab,
				onTabChange: tabState.onTextTabChange,
			}
		case 'bpmn':
			return {
				kind: 'bpmn',
				artifact,
				activeTab: tabState.textActiveTab,
				onTabChange: tabState.onTextTabChange,
			}
		default:
			return { kind: 'unsupported', artifact }
	}
}
